/* DRIVE IQ — ADVENTURE MODE V602.0 DESTINATION IQ — GOOGLE PLACES CANONICAL IDENTITY AUTHORITY
   File: adventure-destination.js

   Purpose:
   - Own selected and committed Adventure destinations.
   - Preserve exact place identity from search through scan and routing.
   - Persist only validated committed destinations.
   - Restore destinations safely without emitting duplicate user actions.
   - Reject invalid or stale destination records.
   - Keep canonical place fields available to Search IQ, Scan IQ, Place IQ,
     Route IQ, and Destination Identity Lock.
   - Keep the destination search field synchronized with selected/committed identity.
   - Restore the canonical city/place label whenever the Adventure panel reopens.
   - Preserve committed results while the user edits or replaces the destination.
   - Clear the visible query only on an explicit destination clear.
   - Treat committed destination and committed results as one scan session.
   - Preserve the canonical destination label whenever its results remain committed.
   - Atomically clear destination, results, pins, selected place, shelf, and Places control.
   - Never clear committed results merely because the field is focused, blurred, or edited.
   - Clear prior results when a different destination is committed.
   - Deduplicates complete provider labels before appending city or region.
   - Produces one idempotent canonical display value for every destination.
   - Prevents labels such as “Destin, Florida, Destin, Florida.”
   - Preserves city, region, and country identity without repeating tokens.
   - Ensures restored, selected, committed, and panel-opened values remain identical.
*/

(function(window){
"use strict";

if(window.DriveIQAdventureDestination){return;}

var cfg=window.DriveIQAdventureConfig;
var utils=window.DriveIQAdventureUtils;
var state=window.DriveIQAdventureState;
var events=window.DriveIQAdventureEvents;

if(!cfg||!utils||!state||!events){
  throw new Error(
    "Destination requires config/utils/state/events."
  );
}

var BUILD="V602_0_DESTINATION_IQ_GOOGLE_PLACES_CANONICAL_IDENTITY_AUTHORITY";
var STORAGE_VERSION=4;
var MAX_RESTORE_AGE_MS=1000*60*60*24*30;

var selectedDestination=null;
var committedDestination=null;
var restored=false;
var editing=false;
var editQuery="";
var lastDisplayValue="";

function now(){
  return Date.now
    ? Date.now()
    : (new Date()).getTime();
}

function clone(value){
  if(value==null){return value;}

  try{
    return JSON.parse(JSON.stringify(value));
  }catch(error){
    return value;
  }
}

function clean(value){
  return String(value==null?"":value)
    .replace(/\s+/g," ")
    .trim();
}

function cleanDisplayPart(value){
  return clean(value)
    .replace(/\s*,\s*/g,", ")
    .replace(/^[,\s]+|[,\s]+$/g,"");
}

function normalizedDisplayKey(value){
  return cleanDisplayPart(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g," ")
    .replace(/\s+/g," ")
    .trim();
}

function deduplicateDisplayValue(value){
  var label=cleanDisplayPart(value);
  var parts;
  var half;
  var left;
  var right;
  var seen={};
  var unique=[];

  if(!label){return "";}

  parts=label.split(",")
    .map(cleanDisplayPart)
    .filter(Boolean);

  if(parts.length>=2&&parts.length%2===0){
    half=parts.length/2;
    left=parts.slice(0,half)
      .map(normalizedDisplayKey)
      .join("|");
    right=parts.slice(half)
      .map(normalizedDisplayKey)
      .join("|");

    if(left&&left===right){
      return parts.slice(0,half).join(", ");
    }
  }

  parts.forEach(function(part){
    var key=normalizedDisplayKey(part);

    if(key&&!seen[key]){
      seen[key]=true;
      unique.push(part);
    }
  });

  return unique.join(", ");
}

function valueAlreadyContains(label,value){
  var labelKey=normalizedDisplayKey(label);
  var valueKey=normalizedDisplayKey(value);

  if(!labelKey||!valueKey){
    return false;
  }

  return (
    labelKey===valueKey||
    labelKey.indexOf(valueKey)>-1
  );
}

function finiteNumber(value){
  var number=Number(value);
  return isFinite(number)?number:null;
}

function latitudeFor(place){
  if(!place){return null;}

  if(place.lat!=null){return finiteNumber(place.lat);}
  if(place.latitude!=null){return finiteNumber(place.latitude);}
  if(place.location&&place.location.latitude!=null){
    return finiteNumber(place.location.latitude);
  }
  if(place.location&&place.location.lat!=null){
    return finiteNumber(place.location.lat);
  }
  if(place.geometry&&place.geometry.location){
    return finiteNumber(
      place.geometry.location.lat!=null
        ? place.geometry.location.lat
        : place.geometry.location.latitude
    );
  }

  return null;
}

function longitudeFor(place){
  if(!place){return null;}

  if(place.lng!=null){return finiteNumber(place.lng);}
  if(place.lon!=null){return finiteNumber(place.lon);}
  if(place.longitude!=null){return finiteNumber(place.longitude);}
  if(place.location&&place.location.longitude!=null){
    return finiteNumber(place.location.longitude);
  }
  if(place.location&&place.location.lng!=null){
    return finiteNumber(place.location.lng);
  }
  if(place.location&&place.location.lon!=null){
    return finiteNumber(place.location.lon);
  }
  if(place.geometry&&place.geometry.location){
    return finiteNumber(
      place.geometry.location.lng!=null
        ? place.geometry.location.lng
        : place.geometry.location.longitude
    );
  }

  return null;
}

function validCoordinate(lat,lng){
  return !!(
    lat!=null&&
    lng!=null&&
    Math.abs(lat)<=90&&
    Math.abs(lng)<=180
  );
}

function googlePlaceIdFor(place){
  var explicit=clean(
    place&&(
      place.googlePlaceId||
      place.google_place_id||
      place.placeId||
      place.place_id||
      ""
    )
  );
  var genericId=clean(place&&place.id||"");
  var provider=clean(
    place&&(
      place.provider||
      place.sourceProvider||
      place.providerName||
      ""
    )
  ).toLowerCase();
  var raw=explicit;

  if(!raw&&(genericId.indexOf("places/")===0||provider.indexOf("google")>-1)){
    raw=genericId;
  }

  /* Google Places (New) may return resource names such as places/ChIJ... */
  return raw.indexOf("places/")===0
    ? raw.slice("places/".length)
    : raw;
}

function mapboxIdFor(place){
  return clean(
    place&&(
      place.mapboxId||
      place.mapbox_id||
      place.mapboxFeatureId||
      ""
    )
  );
}

function providerFor(place){
  var explicit=clean(
    place&&(
      place.provider||
      place.sourceProvider||
      place.providerName||
      ""
    )
  ).toLowerCase();

  if(googlePlaceIdFor(place)){return "google";}
  if(mapboxIdFor(place)){return "mapbox";}
  if(explicit){return explicit;}
  return "unknown";
}

function placeIdFor(place){
  return googlePlaceIdFor(place)||mapboxIdFor(place)||clean(place&&place.id||"");
}

function keyFor(place){
  var lat=latitudeFor(place);
  var lng=longitudeFor(place);

  if(!place||!validCoordinate(lat,lng)){
    return "";
  }

  return [
    placeIdFor(place)||
      clean(place.name||place.label||""),
    String(lat),
    String(lng)
  ].join("|");
}

function committedResultSessionKey(){
  return clean(
    state.get("results.destinationKey","")||
    state.get("results.session.destinationKey","")||
    state.get("scan.session.destinationKey","")||
    ""
  );
}

function clearLinkedResults(reason,previousDestination){
  var source=String(reason||"destination-cleared");
  var previous=clone(previousDestination||null);

  try{
    state.merge("results",{
      items:[],
      count:0,
      preferred:null,
      committed:false,
      destinationKey:"",
      destination:null,
      query:"",
      category:"",
      session:null,
      clearedAt:now(),
      clearReason:source
    },{
      action:"destination:clear-linked-results"
    });
  }catch(error){}

  try{
    state.merge("scan",{
      committedResults:[],
      lastResults:[],
      session:null,
      activeResultSet:null
    },{
      action:"destination:clear-scan-session"
    });
  }catch(error){}

  try{
    state.set("selectedPlace",null,{
      action:"destination:clear-selected-place"
    });
  }catch(error){}

  try{
    state.set("place.selected",null,{
      action:"destination:clear-place-selected"
    });
  }catch(error){}

  try{
    state.merge("placeCard",{
      item:null,
      visible:false,
      open:false
    },{
      action:"destination:clear-place-card"
    });
  }catch(error){}

  emit("results:cleared",{
    reason:source,
    destination:previous
  });

  emit("pins:clear-requested",{
    reason:source,
    destination:previous
  });

  emit("results:shelf-close-requested",{
    reason:source,
    userClosed:false
  });

  emit("ui:results-pill-hide-requested",{
    reason:source
  });

  try{
    if(
      window.DriveIQAdventureScan&&
      typeof window.DriveIQAdventureScan.closeResultShelf==="function"
    ){
      window.DriveIQAdventureScan.closeResultShelf(source);
    }
  }catch(error){}

  try{
    if(
      window.DriveIQAdventurePins&&
      typeof window.DriveIQAdventurePins.clear==="function"
    ){
      window.DriveIQAdventurePins.clear(source);
    }
  }catch(error){}

  try{
    if(
      window.DriveIQAdventureUI&&
      typeof window.DriveIQAdventureUI.hideResultsPill==="function"
    ){
      window.DriveIQAdventureUI.hideResultsPill();
    }

    if(
      window.DriveIQAdventureUI&&
      typeof window.DriveIQAdventureUI.hideAllResultShelves==="function"
    ){
      window.DriveIQAdventureUI.hideAllResultShelves();
    }
  }catch(error){}

  return true;
}

function bindResultSession(destination,source){
  var active=destination||current();
  var key=active?keyFor(active):"";

  if(!active||!key){
    return false;
  }

  try{
    state.merge("results",{
      destinationKey:key,
      destination:clone(active),
      destinationLabel:canonicalDisplayValue(active),
      canonicalDisplayValue:canonicalDisplayValue(active),
      destinationIQ:intelligence(active),
      sessionCommittedAt:now()
    },{
      action:"destination:bind-results-session"
    });
  }catch(error){}

  try{
    state.merge("scan.session",{
      destinationKey:key,
      destination:clone(active),
      destinationLabel:canonicalDisplayValue(active),
      canonicalDisplayValue:canonicalDisplayValue(active),
      destinationIQ:intelligence(active),
      source:String(source||"destination-commit")
    },{
      action:"destination:bind-scan-session"
    });
  }catch(error){}

  emit("destination:session-bound",{
    destination:clone(active),
    destinationKey:key,
    displayValue:canonicalDisplayValue(active),
    source:String(source||"destination-commit")
  });

  return true;
}

function normalizedTypes(place){
  var types=place&&place.types;
  var output=[];
  var primary=clean(
    place&&(
      place.primaryType||
      place.primary_type||
      place.primaryCategory||
      ""
    )
  );

  if(primary){output.push(primary);}

  if(Array.isArray(types)){
    output=output.concat(types.map(clean).filter(Boolean));
  }else if(typeof types==="string"){
    output=output.concat(types.split(",").map(clean).filter(Boolean));
  }

  return output.filter(function(type,index,list){
    return type&&list.indexOf(type)===index;
  });
}

function localizedText(value){
  if(value==null){return "";}
  if(typeof value==="string"){return clean(value);}
  if(typeof value==="object"){
    return clean(value.text||value.value||value.name||"");
  }
  return clean(value);
}

function photoReferencesFor(place){
  var photos=place&&place.photos;
  if(!Array.isArray(photos)){return [];}

  return photos.map(function(photo){
    if(typeof photo==="string"){
      return {name:clean(photo),widthPx:null,heightPx:null,authorAttributions:[]};
    }
    return {
      name:clean(photo&&(
        photo.name||photo.photoReference||photo.photo_reference||photo.reference||""
      )),
      widthPx:finiteNumber(photo&&(
        photo.widthPx!=null?photo.widthPx:photo.width
      )),
      heightPx:finiteNumber(photo&&(
        photo.heightPx!=null?photo.heightPx:photo.height
      )),
      authorAttributions:Array.isArray(photo&&photo.authorAttributions)
        ? clone(photo.authorAttributions)
        : []
    };
  }).filter(function(photo){return !!photo.name;});
}

function openingHoursFor(place){
  var hours=firstObject(
    place&&place.regularOpeningHours,
    place&&place.currentOpeningHours,
    place&&place.openingHours,
    place&&place.opening_hours
  );
  return hours?clone(hours):null;
}


function firstObject(){
  var i;
  for(i=0;i<arguments.length;i+=1){
    if(arguments[i]&&typeof arguments[i]==="object"){
      return arguments[i];
    }
  }
  return null;
}

function pointFrom(value){
  var lat;
  var lng;

  if(!value||typeof value!=="object"){
    return null;
  }

  lat=finiteNumber(
    value.lat!=null?value.lat:
    value.latitude
  );
  lng=finiteNumber(
    value.lng!=null?value.lng:
    value.lon!=null?value.lon:
    value.longitude
  );

  return validCoordinate(lat,lng)
    ? {lat:lat,lng:lng}
    : null;
}

function boundsFrom(place){
  var source=firstObject(
    place&&place.bounds,
    place&&place.viewport,
    place&&place.boundingBox,
    place&&place.bounding_box,
    place&&place.geometry&&place.geometry.viewport
  );
  var sw;
  var ne;
  var north;
  var south;
  var east;
  var west;

  if(!source){return null;}

  sw=pointFrom(source.southwest||source.southWest||source.sw||source.low);
  ne=pointFrom(source.northeast||source.northEast||source.ne||source.high);

  if(sw&&ne&&sw.lat<=ne.lat){
    return {southwest:sw,northeast:ne};
  }

  north=finiteNumber(source.north);
  south=finiteNumber(source.south);
  east=finiteNumber(source.east);
  west=finiteNumber(source.west);

  if(
    validCoordinate(north,east)&&
    validCoordinate(south,west)&&
    south<=north
  ){
    return {
      southwest:{lat:south,lng:west},
      northeast:{lat:north,lng:east}
    };
  }

  return null;
}

function centerOfBounds(bounds){
  if(!bounds||!bounds.southwest||!bounds.northeast){return null;}
  return {
    lat:(bounds.southwest.lat+bounds.northeast.lat)/2,
    lng:(bounds.southwest.lng+bounds.northeast.lng)/2
  };
}

function uniquePoints(points){
  var seen={};
  var output=[];

  (points||[]).forEach(function(point){
    var normalized=pointFrom(point);
    var key;

    if(!normalized){return;}

    key=normalized.lat.toFixed(5)+"|"+normalized.lng.toFixed(5);
    if(seen[key]){return;}

    seen[key]=true;
    output.push({
      type:clean(point.type||"anchor"),
      lat:normalized.lat,
      lng:normalized.lng,
      confidence:finiteNumber(point.confidence),
      source:clean(point.source||"")
    });
  });

  return output;
}

function classifyDestination(place){
  var text=[
    place&&place.entityType,
    place&&place.category,
    place&&place.name,
    place&&place.label
  ].concat(normalizedTypes(place)).join(" ").toLowerCase();

  if(/\b(lake|reservoir|lagoon|river|bay|harbor|harbour|marina)\b/.test(text)){
    return "water-region";
  }
  if(/\b(national park|state park|park|forest|preserve|wildlife refuge)\b/.test(text)){
    return "park-region";
  }
  if(/\b(neighborhood|neighbourhood|district|borough|quarter|ward)\b/.test(text)){
    return "district";
  }
  if(/\b(island|isle)\b/.test(text)){
    return "island";
  }
  if(/\b(city|municipality|metropolis)\b/.test(text)){
    return "city";
  }
  if(/\b(town|village|hamlet)\b/.test(text)){
    return "town";
  }
  if(/\b(address|street_address|premise|establishment|point_of_interest|landmark)\b/.test(text)){
    return "place";
  }

  if(place&&place.city&&place.name&&normalizedDisplayKey(place.city)===normalizedDisplayKey(place.name)){
    return "city";
  }

  return "destination";
}

function approximateAreaKm2(bounds){
  var latSpan;
  var lngSpan;
  var midLat;
  var height;
  var width;

  if(!bounds){return null;}

  latSpan=Math.abs(bounds.northeast.lat-bounds.southwest.lat);
  lngSpan=Math.abs(bounds.northeast.lng-bounds.southwest.lng);
  midLat=(bounds.northeast.lat+bounds.southwest.lat)/2;
  height=latSpan*111.32;
  width=lngSpan*111.32*Math.max(0.15,Math.cos(midLat*Math.PI/180));

  return Math.round(height*width*10)/10;
}

function marketClassFor(place,bounds,destinationType){
  var explicit=clean(
    place&&(
      place.marketClass||
      place.market_class||
      place.marketSize||
      place.market_size
    )
  ).toLowerCase();
  var population=finiteNumber(place&&place.population);
  var area=approximateAreaKm2(bounds);

  if(explicit){return explicit;}
  if(destinationType==="water-region"){return "water-destination";}
  if(destinationType==="park-region"){return "park-destination";}
  if(destinationType==="district"){return "district";}

  if(population!=null){
    if(population>=3000000){return "major-metro";}
    if(population>=750000){return "large-city";}
    if(population>=150000){return "city";}
    if(population>=25000){return "town";}
    return "small-town";
  }

  if(area!=null){
    if(area>=1200){return "major-metro";}
    if(area>=350){return "large-city";}
    if(area>=80){return "city";}
    if(area>=15){return "town";}
    return "small-town";
  }

  if(destinationType==="city"){return "city";}
  if(destinationType==="town"){return "town";}
  return "unknown";
}

function expectedInventoryFor(marketClass,destinationType){
  var profiles={
    "small-town":{food:12,coffee:6,dessert:4,nightlife:4,outdoors:6,golf:3,default:8},
    "town":{food:20,coffee:10,dessert:7,nightlife:8,outdoors:9,golf:5,default:12},
    "city":{food:35,coffee:18,dessert:12,nightlife:18,outdoors:14,golf:8,default:18},
    "large-city":{food:50,coffee:28,dessert:18,nightlife:30,outdoors:20,golf:12,default:25},
    "major-metro":{food:75,coffee:40,dessert:25,nightlife:45,outdoors:30,golf:16,default:35},
    "district":{food:30,coffee:16,dessert:10,nightlife:20,outdoors:8,golf:3,default:15},
    "water-destination":{food:24,coffee:10,dessert:8,nightlife:12,outdoors:30,golf:6,default:18},
    "park-destination":{food:10,coffee:5,dessert:3,nightlife:2,outdoors:35,golf:3,default:12},
    "unknown":{food:25,coffee:12,dessert:8,nightlife:12,outdoors:12,golf:6,default:14}
  };
  var selected=profiles[marketClass]||profiles.unknown;

  if(destinationType==="island"&&marketClass==="unknown"){
    selected=profiles["water-destination"];
  }

  return clone(selected);
}

function strategyFor(marketClass,destinationType,bounds){
  var multiAnchor=(
    marketClass==="large-city"||
    marketClass==="major-metro"||
    destinationType==="district"||
    destinationType==="water-region"||
    destinationType==="park-region"||
    destinationType==="island"
  );

  return {
    mode:destinationType==="water-region"
      ? "shoreline-and-access"
      : destinationType==="park-region"
        ? "boundary-and-access"
        : multiAnchor
          ? "multi-anchor"
          : "single-center-expand",
    useBounds:!!bounds,
    multiAnchor:multiAnchor,
    preserveBoundary:destinationType==="district",
    allowRadiusExpansion:true,
    recoveryEnabled:true,
    maxRecoveryRounds:marketClass==="major-metro"?4:3
  };
}

function searchAnchorsFor(place,bounds){
  var center={type:"provider-center",lat:latitudeFor(place),lng:longitudeFor(place),confidence:place.confidence,source:place.provider};
  var boundsCenter=centerOfBounds(bounds);
  var anchors=[center];
  var commercial=pointFrom(
    place&&(
      place.commercialCenter||
      place.commercial_center||
      place.populationCenter||
      place.population_center
    )
  );

  if(commercial){
    anchors.push({type:"commercial-center",lat:commercial.lat,lng:commercial.lng,confidence:finiteNumber(place.commercialCenterConfidence),source:"destination"});
  }

  if(boundsCenter){
    anchors.push({type:"bounds-center",lat:boundsCenter.lat,lng:boundsCenter.lng,confidence:null,source:"bounds"});

    anchors.push({type:"northwest",lat:bounds.northeast.lat,lng:bounds.southwest.lng,source:"bounds"});
    anchors.push({type:"northeast",lat:bounds.northeast.lat,lng:bounds.northeast.lng,source:"bounds"});
    anchors.push({type:"southwest",lat:bounds.southwest.lat,lng:bounds.southwest.lng,source:"bounds"});
    anchors.push({type:"southeast",lat:bounds.southwest.lat,lng:bounds.northeast.lng,source:"bounds"});
  }

  if(Array.isArray(place&&place.searchAnchors)){
    anchors=anchors.concat(place.searchAnchors);
  }

  return uniquePoints(anchors);
}

function confidenceProfile(place,bounds,destinationType){
  var identity=finiteNumber(place&&(
    place.identityConfidence!=null?place.identityConfidence:
    place.confidence
  ));
  var geometry=finiteNumber(place&&place.geometryConfidence);
  var reasons=[];

  if(identity==null){identity=placeIdFor(place)?0.82:0.68;}
  if(geometry==null){geometry=bounds?0.86:0.65;}

  if(!placeIdFor(place)){reasons.push("provider-place-id-missing");}
  if(!bounds){reasons.push("bounds-missing");}
  if(destinationType==="destination"){reasons.push("destination-type-uncertain");}

  return {
    identity:Math.max(0,Math.min(1,identity)),
    geometry:Math.max(0,Math.min(1,geometry)),
    commercialCenter:pointFrom(place&&place.commercialCenter)?0.85:0.45,
    overall:Math.round(((identity*0.55)+(geometry*0.45))*100)/100,
    status:(identity>=0.75&&geometry>=0.65)?"verified":"provisional",
    reasons:reasons
  };
}

function buildDestinationIQ(place){
  var bounds=boundsFrom(place);
  var destinationType=classifyDestination(place);
  var marketClass=marketClassFor(place,bounds,destinationType);
  var expectedInventory=expectedInventoryFor(marketClass,destinationType);
  var strategy=strategyFor(marketClass,destinationType,bounds);
  var anchors=searchAnchorsFor(place,bounds);
  var confidence=confidenceProfile(place,bounds,destinationType);

  return {
    version:1,
    destinationType:destinationType,
    marketClass:marketClass,
    center:{lat:latitudeFor(place),lng:longitudeFor(place)},
    bounds:bounds,
    approximateAreaKm2:approximateAreaKm2(bounds),
    searchAnchors:anchors,
    scanStrategy:strategy,
    expectedInventory:expectedInventory,
    confidence:confidence,
    recovery:{
      enabled:true,

      /*
        Inventory ratios are advisory only. They may guide optional enrichment,
        but they must never prevent the first usable result set from completing.
      */
      triggerBelowRatio:0.55,
      hardFailureBelowRatio:0.20,

      /*
        Visible scan completion is controlled by the shared minimum usable
        result threshold, not by expected market inventory.
      */
      /*
        This is the global floor only. recoveryDecision() raises it by
        category and expected market inventory so one or two places can never
        be treated as a healthy destination scan.
      */
      minimumUsableResults:Math.max(
        6,
        Number(
          cfg&&
          cfg.scan&&
          cfg.scan.minimumVisibleResults||
          6
        )
      ),

      broadenQueries:true,
      addAnchors:true,
      expandRadius:true,
      retryAlternateProvider:true
    },
    generatedAt:now()
  };
}

function intelligence(destination){
  var active=destination||current();
  return active?clone(active.destinationIQ||buildDestinationIQ(active)):null;
}

function expectedFor(category,destination){
  var iq=intelligence(destination);
  var key=clean(category||"default").toLowerCase();

  if(!iq){return 0;}
  return Number(iq.expectedInventory[key]||iq.expectedInventory.default||0);
}

function normalizedCategoryKey(category){
  var key=clean(category||"default").toLowerCase();

  if(/food|restaurant|dining|eat/.test(key)){return "food";}
  if(/coffee|cafe/.test(key)){return "coffee";}
  if(/dessert|treat|bakery|ice cream/.test(key)){return "dessert";}
  if(/nightlife|bar|pub|club/.test(key)){return "nightlife";}
  if(/outdoor|nature|park|trail|water/.test(key)){return "outdoors";}
  if(/golf/.test(key)){return "golf";}

  return key||"default";
}

function minimumUsableFor(category,destination){
  var iq=intelligence(destination);
  var key=normalizedCategoryKey(category);
  var expected=expectedFor(key,destination);
  var configured=Math.max(
    1,
    Number(
      cfg&&
      cfg.scan&&
      cfg.scan.minimumVisibleResults||
      1
    )
  );
  var sharedFloor=Math.max(
    1,
    Number(
      iq&&
      iq.recovery&&
      iq.recovery.minimumUsableResults||
      configured
    )
  );
  var categoryFloor={
    food:10,
    coffee:6,
    dessert:5,
    nightlife:6,
    outdoors:6,
    golf:4,
    default:8
  }[key]||8;
  var categoryCeiling={
    food:18,
    coffee:12,
    dessert:10,
    nightlife:14,
    outdoors:14,
    golf:8,
    default:12
  }[key]||12;
  var inventoryTarget=expected>0
    ? Math.ceil(expected*0.35)
    : categoryFloor;

  return Math.max(
    sharedFloor,
    Math.min(categoryCeiling,Math.max(categoryFloor,inventoryTarget))
  );
}

function recoveryDecision(category,foundCount,destination){
  var iq=intelligence(destination);
  var expected=expectedFor(category,destination);
  var found=Math.max(0,Number(foundCount||0));
  var ratio=expected>0?found/expected:1;
  var minimumUsable=minimumUsableFor(category,destination);
  var usable=found>=minimumUsable;
  var inventoryShortfall=!!(
    iq&&
    iq.recovery&&
    iq.recovery.enabled&&
    expected>0&&
    ratio<iq.recovery.triggerBelowRatio
  );

  /*
    Critical authority:
    Recovery may block completion only when there are not enough usable
    results. Expected market inventory remains advisory and can drive later
    background enrichment, but it cannot keep the global SCANNING state alive.
  */
  return {
    expected:expected,
    found:found,
    minimumUsableResults:minimumUsable,
    usable:usable,
    ratio:Math.round(ratio*100)/100,

    recover:!!(
      iq&&
      iq.recovery&&
      iq.recovery.enabled&&
      !usable
    ),

    severe:!!(
      iq&&
      iq.recovery&&
      iq.recovery.enabled&&
      found===0
    ),

    inventoryShortfall:inventoryShortfall,
    backgroundEnrichmentRecommended:!!(
      usable&&
      inventoryShortfall
    ),

    strategy:iq?clone(iq.scanStrategy):null,
    anchors:iq?clone(iq.searchAnchors):[]
  };
}

function normalize(place,options){
  var supplied=options||{};
  var lat;
  var lng;
  var normalized;
  var timestamp;
  var googleId;
  var mapboxId;
  var displayName;
  var formattedAddress;
  var photos;

  if(!place){return null;}

  lat=latitudeFor(place);
  lng=longitudeFor(place);

  if(!validCoordinate(lat,lng)){
    return null;
  }

  timestamp=finiteNumber(
    place.committedAt||
    place.selectedAt||
    place.updatedAt||
    supplied.timestamp
  )||now();

  googleId=googlePlaceIdFor(place);
  mapboxId=mapboxIdFor(place);
  displayName=localizedText(
    place.displayName||
    place.display_name||
    place.name||
    place.label||
    "Adventure Destination"
  );
  formattedAddress=clean(
    place.formattedAddress||
    place.formatted_address||
    place.address||
    place.fullAddress||
    ""
  );
  photos=photoReferencesFor(place);

  normalized={
    /* Canonical provider identity. Google wins whenever a Google Place ID exists. */
    placeId:googleId||mapboxId||placeIdFor(place),
    googlePlaceId:googleId,
    mapboxId:mapboxId,
    provider:providerFor(place),
    providerPlaceId:googleId||mapboxId||clean(
      place.providerPlaceId||
      place.provider_place_id||
      ""
    ),

    displayName:deduplicateDisplayValue(displayName),
    name:deduplicateDisplayValue(displayName),
    label:deduplicateDisplayValue(
      localizedText(place.label)||displayName
    ),
    formattedAddress:formattedAddress,
    address:formattedAddress,

    city:clean(place.city||place.locality||place.addressComponents&&place.addressComponents.city||""),
    region:clean(place.region||place.state||place.administrativeArea||""),
    postalCode:clean(place.postalCode||place.postcode||place.zip||""),
    country:clean(place.country||place.countryCode||""),

    primaryType:clean(
      place.primaryType||
      place.primary_type||
      place.primaryCategory||
      place.category||
      ""
    ),
    primaryTypeDisplayName:localizedText(place.primaryTypeDisplayName),
    category:clean(
      place.category||
      place.primaryCategory||
      place.primaryType||
      place.userFacingCategory||
      ""
    ),
    entityType:clean(
      place.entityType||
      place.entity_type||
      place.type||
      place.primaryType||
      ""
    ),
    types:normalizedTypes(place),

    lat:lat,
    lng:lng,
    location:{latitude:lat,longitude:lng},
    viewport:boundsFrom(place),
    confidence:finiteNumber(
      place.confidence||
      place.identityConfidence||
      place.locationConfidence
    ),

    rating:finiteNumber(place.rating),
    userRatingCount:finiteNumber(
      place.userRatingCount!=null
        ? place.userRatingCount
        : place.user_ratings_total
    ),
    businessStatus:clean(place.businessStatus||place.business_status||""),
    regularOpeningHours:openingHoursFor(place),
    websiteUri:clean(place.websiteUri||place.website||place.url||""),
    nationalPhoneNumber:clean(place.nationalPhoneNumber||place.formatted_phone_number||""),
    internationalPhoneNumber:clean(place.internationalPhoneNumber||place.international_phone_number||""),
    googleMapsUri:clean(place.googleMapsUri||place.google_maps_uri||""),
    priceLevel:clean(place.priceLevel||place.price_level||""),
    photos:photos,

    entranceLat:finiteNumber(place.entranceLat||place.entrance_lat),
    entranceLng:finiteNumber(place.entranceLng||place.entrance_lng),
    photoUrl:clean(
      place.diqPhotoUrl||
      place.photoUrl||
      place.photoURL||
      ""
    ),
    source:clean(supplied.source||place.source||""),
    selectedAt:finiteNumber(place.selectedAt)||timestamp,
    committedAt:finiteNumber(place.committedAt)||0,
    updatedAt:timestamp
  };

  normalized.destinationIQ=buildDestinationIQ(normalized);
  normalized.key=keyFor(normalized);

  if(!normalized.key||!normalized.name){
    return null;
  }

  return normalized;
}


function canonicalDisplayValue(destination){
  var place=destination||current();
  var name;
  var city;
  var region;
  var country;
  var parts=[];
  var candidate;

  if(!place){return "";}

  name=deduplicateDisplayValue(
    place.canonicalLabel||
    place.displayName||
    place.formattedAddress||
    place.label||
    place.name||
    ""
  );
  city=cleanDisplayPart(place.city||place.locality||"");
  region=cleanDisplayPart(
    place.region||
    place.state||
    place.administrativeArea||
    ""
  );
  country=cleanDisplayPart(place.country||place.countryCode||"");

  /*
    The provider may already supply a complete label such as
    "Destin, Florida". Treat that as authoritative and only append missing
    identity parts. Calling this repeatedly must always return the same value.
  */
  if(name){
    parts.push(name);
  }

  candidate=parts.join(", ");

  if(
    city&&
    !valueAlreadyContains(candidate,city)
  ){
    parts.push(city);
    candidate=parts.join(", ");
  }

  if(
    region&&
    !valueAlreadyContains(candidate,region)
  ){
    parts.push(region);
    candidate=parts.join(", ");
  }

  if(
    country&&
    parts.length<2&&
    !valueAlreadyContains(candidate,country)
  ){
    parts.push(country);
  }

  return deduplicateDisplayValue(
    parts.join(", ")
  );
}

function setDisplayState(value,source,options){
  var next=deduplicateDisplayValue(value);
  var supplied=options||{};

  lastDisplayValue=next;

  try{
    state.merge("destination",{
      displayValue:next,
      query:next,
      editQuery:editing?editQuery:"",
      editing:editing,
      displaySource:String(source||"destination"),
      preserveCommittedResults:
        supplied.preserveCommittedResults!==false
    },{
      action:"destination:display-sync"
    });
  }catch(error){
    try{
      state.set("destination.displayValue",next,{
        action:"destination:display-sync"
      });
      state.set("destination.query",next,{
        action:"destination:display-sync"
      });
      state.set("destination.editing",editing,{
        action:"destination:display-sync"
      });
    }catch(ignore){}
  }

  emit("destination:display-changed",{
    value:next,
    source:String(source||"destination"),
    editing:editing,
    preserveCommittedResults:
      supplied.preserveCommittedResults!==false
  });

  return next;
}

function syncDisplay(source){
  var active=current();
  var value;

  if(editing){
    value=editQuery;
  }else{
    value=canonicalDisplayValue(active);
  }

  return setDisplayState(value,source||"sync",{
    preserveCommittedResults:true
  });
}

function beginEdit(value,source){
  var supplied=value;
  var active=current();

  editing=true;

  if(supplied===undefined||supplied===null){
    editQuery=canonicalDisplayValue(active);
  }else{
    editQuery=String(supplied==null?"":supplied);
  }

  setDisplayState(editQuery,source||"begin-edit",{
    preserveCommittedResults:true
  });

  emit("destination:edit-started",{
    value:editQuery,
    destination:clone(active),
    source:String(source||"begin-edit")
  });

  return editQuery;
}

function updateEditQuery(value,source){
  if(!editing){
    editing=true;
  }

  editQuery=String(value==null?"":value);

  setDisplayState(editQuery,source||"edit-query",{
    preserveCommittedResults:true
  });

  emit("destination:edit-query-changed",{
    value:editQuery,
    source:String(source||"edit-query"),
    hasCommittedDestination:!!committed()
  });

  return editQuery;
}

function cancelEdit(source){
  editing=false;
  editQuery="";

  syncDisplay(source||"cancel-edit");

  emit("destination:edit-cancelled",{
    value:lastDisplayValue,
    destination:current(),
    source:String(source||"cancel-edit")
  });

  return lastDisplayValue;
}

function finishEdit(source){
  editing=false;
  editQuery="";

  syncDisplay(source||"finish-edit");

  emit("destination:edit-finished",{
    value:lastDisplayValue,
    destination:current(),
    source:String(source||"finish-edit")
  });

  return lastDisplayValue;
}

function displayValue(){
  if(editing){
    return editQuery;
  }

  return lastDisplayValue||
    canonicalDisplayValue(current());
}

function storageKey(){
  return (
    cfg.storage&&
    cfg.storage.committedAdventureDestination
  )||"driveiq_adventure_destination";
}

function persist(destination){
  var record;

  if(!destination){return false;}

  record={
    version:STORAGE_VERSION,
    savedAt:now(),
    destination:clone(destination)
  };

  try{
    window.localStorage.setItem(
      storageKey(),
      JSON.stringify(record)
    );
    return true;
  }catch(error){
    return false;
  }
}

function removePersisted(){
  try{
    window.localStorage.removeItem(storageKey());
    return true;
  }catch(error){
    return false;
  }
}

function setStateDestination(destination,source,committed){
  try{
    state.setDestination(
      destination,
      source,
      committed===true
    );
    return true;
  }catch(error){
    return false;
  }
}

function emit(name,payload){
  try{
    events.emit(name,payload);
    return true;
  }catch(error){
    return false;
  }
}

function select(place,source){
  var normalized=normalize(place,{
    source:source||"selection"
  });

  if(!normalized){
    emit("destination:rejected",{
      reason:"invalid-selection",
      source:String(source||"selection")
    });
    return false;
  }

  normalized.selectedAt=now();
  normalized.updatedAt=normalized.selectedAt;
  normalized.source=String(source||"selection");

  selectedDestination=normalized;
  editing=false;
  editQuery="";

  setStateDestination(
    clone(normalized),
    normalized.source,
    false
  );

  syncDisplay("destination-selected");

  emit("destination:selected",{
    destination:clone(normalized),
    source:normalized.source,
    key:normalized.key,
    selectedAt:normalized.selectedAt,
    displayValue:lastDisplayValue
  });

  return clone(normalized);
}

function commit(place,reason){
  var source=String(reason||"commit");
  var previous=committed();
  var previousKey=previous?keyFor(previous):"";
  var candidate=place||selectedDestination||
    state.get("destination.selected",null);
  var normalized=normalize(candidate,{
    source:source
  });

  if(!normalized){
    emit("destination:rejected",{
      reason:"invalid-commit",
      source:source
    });
    return false;
  }

  normalized.selectedAt=finiteNumber(
    normalized.selectedAt
  )||now();
  normalized.committedAt=now();
  normalized.updatedAt=normalized.committedAt;
  normalized.source=source;

  if(
    previousKey&&
    normalized.key&&
    previousKey!==normalized.key
  ){
    clearLinkedResults(
      "destination-replaced",
      previous
    );
  }

  selectedDestination=clone(normalized);
  committedDestination=clone(normalized);
  editing=false;
  editQuery="";

  setStateDestination(
    clone(normalized),
    source,
    true
  );

  persist(normalized);
  syncDisplay("destination-committed");
  bindResultSession(normalized,source);

  emit("destination:committed",{
    destination:clone(normalized),
    source:source,
    key:normalized.key,
    committedAt:normalized.committedAt,
    displayValue:lastDisplayValue
  });

  return clone(normalized);
}

function restore(){
  var raw;
  var parsed;
  var record;
  var normalized;
  var age;

  if(restored){
    return clone(committedDestination);
  }

  restored=true;

  try{
    raw=window.localStorage.getItem(storageKey());
  }catch(error){
    raw=null;
  }

  if(!raw){return null;}

  try{
    parsed=JSON.parse(raw);
  }catch(error){
    removePersisted();
    return null;
  }

  /*
    Backward compatibility:
    older builds stored the destination object directly.
  */
  record=parsed&&parsed.destination
    ? parsed
    : {
      version:1,
      savedAt:parsed&&(
        parsed.committedAt||
        parsed.updatedAt
      ),
      destination:parsed
    };

  age=now()-Number(record.savedAt||0);

  if(
    record.savedAt&&
    (
      !isFinite(age)||
      age<0||
      age>MAX_RESTORE_AGE_MS
    )
  ){
    removePersisted();
    return null;
  }

  normalized=normalize(record.destination,{
    source:"restore",
    timestamp:Number(record.savedAt||now())
  });

  if(!normalized){
    removePersisted();
    return null;
  }

  normalized.source="restore";
  normalized.committedAt=finiteNumber(
    normalized.committedAt
  )||Number(record.savedAt||now());
  normalized.updatedAt=now();

  selectedDestination=clone(normalized);
  committedDestination=clone(normalized);
  editing=false;
  editQuery="";

  setStateDestination(
    clone(normalized),
    "restore",
    true
  );

  syncDisplay("destination-restored");
  bindResultSession(normalized,"restore");

  emit("destination:restored",{
    destination:clone(normalized),
    key:normalized.key,
    restoredAt:now(),
    displayValue:lastDisplayValue
  });

  return clone(normalized);
}

function clear(reason){
  var source=String(reason||"clear");
  var previous=current();

  selectedDestination=null;
  committedDestination=null;
  editing=false;
  editQuery="";
  lastDisplayValue="";

  setStateDestination(
    null,
    source,
    false
  );

  removePersisted();

  try{
    state.merge("destination",{
      selected:null,
      committed:null,
      current:null,
      displayValue:"",
      query:"",
      editQuery:"",
      editing:false,
      clearedAt:now(),
      clearReason:source,
      preserveCommittedResults:false
    },{
      action:"destination:clear-session"
    });
  }catch(error){}

  setDisplayState("",source,{
    preserveCommittedResults:false
  });

  clearLinkedResults(source,previous);

  emit("destination:cleared",{
    source:source,
    previous:clone(previous),
    clearedAt:now(),
    resultsCleared:true
  });

  emit("destination:session-cleared",{
    source:source,
    previous:clone(previous),
    clearedAt:now()
  });

  return true;
}

function selected(){
  return clone(
    selectedDestination||
    state.get("destination.selected",null)||
    null
  );
}

function committed(){
  return clone(
    committedDestination||
    state.get("destination.committed",null)||
    null
  );
}

function current(){
  return committed()||selected()||null;
}

function matches(place){
  var candidate=normalize(place);
  var active=current();

  return !!(
    candidate&&
    active&&
    candidate.key===active.key
  );
}


function panelOpened(source){
  var active=current();
  var value=syncDisplay(source||"panel-opened");

  if(active){
    bindResultSession(active,source||"panel-opened");
  }

  emit("destination:panel-synchronized",{
    value:value,
    destination:current(),
    editing:editing,
    source:String(source||"panel-opened")
  });

  return value;
}

window.DriveIQAdventureDestination={
  normalize:normalize,
  googlePlaceIdFor:googlePlaceIdFor,
  mapboxIdFor:mapboxIdFor,
  providerFor:providerFor,
  keyFor:keyFor,
  select:select,
  commit:commit,
  clear:clear,
  current:current,
  selected:selected,
  committed:committed,
  matches:matches,
  restore:restore,
  canonicalDisplayValue:canonicalDisplayValue,
  deduplicateDisplayValue:deduplicateDisplayValue,
  normalizedDisplayKey:normalizedDisplayKey,
  displayValue:displayValue,
  syncDisplay:syncDisplay,
  panelOpened:panelOpened,
  beginEdit:beginEdit,
  updateEditQuery:updateEditQuery,
  cancelEdit:cancelEdit,
  finishEdit:finishEdit,
  bindResultSession:bindResultSession,
  clearLinkedResults:clearLinkedResults,
  committedResultSessionKey:committedResultSessionKey,
  intelligence:intelligence,
  expectedFor:expectedFor,
  minimumUsableFor:minimumUsableFor,
  recoveryDecision:recoveryDecision,
  buildDestinationIQ:buildDestinationIQ,

  status:function(){
    var active=current();

    return {
      build:BUILD,
      restored:restored,
      selectedKey:selectedDestination
        ? selectedDestination.key
        : "",
      committedKey:committedDestination
        ? committedDestination.key
        : "",
      activeKey:active
        ? active.key
        : "",
      displayValue:displayValue(),
      editing:editing,
      editQuery:editQuery,
      resultSessionKey:committedResultSessionKey(),
      destinationIQ:intelligence(active),
      storageKey:storageKey()
    };
  }
};

restore();
syncDisplay("module-ready");

console.log(
  "[Drive IQ Adventure V600]",
  "Destination IQ Google Places canonical identity authority loaded"
);

})(window);
